import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  Card,
  Button,
  Select,
  Table,
  Typography,
  Row,
  Col,
  Spin,
  notification,
  Divider,
  Switch,
  Space,
} from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import {
  getCreditById,
  getCreditPartnerInvoices,
  getFirstPagePartnerInvoicesForCredit,
  allocateCredit,
  getAllPartners,
  clearAccountingCache,
  clearPartnerInvoicesCache,
} from "../../../api/accounting";
import { getCreditColumns } from "../Columns/CreditAllocation/creditColumns";
import { getInvoiceColumns } from "../Columns/CreditAllocation/invoiceColumns";
import "bootstrap/dist/css/bootstrap.min.css";
import "../Utils/BankStatements.css"; // reusing the same CSS for consistent styling
const { Text, Title } = Typography;
const { Option } = Select;

const getDecimalPrecision = () => {
  try { const s = localStorage.getItem("global_decimal_precision"); return s ? parseInt(s, 10) : 3; } catch { return 3; }
};

// Helper to parse numbers that may contain commas, dollar signs, or other formatting
// parseFloat("18,132") returns 18, but we want 18132
// parseFloat("$20,670.60") returns NaN, but we want 20670.60
const parseNumberWithCommas = (value) => {
  if (value == null || value === undefined || value === "") return 0;
  // Convert to string and remove currency symbols ($, €, £, etc.), commas, and spaces
  const cleanedValue = String(value)
    .replace(/[$€£¥₹,\s]/g, "") // Remove currency symbols, commas, and spaces
    .trim();
  const parsed = parseFloat(cleanedValue);
  return isNaN(parsed) ? 0 : parsed;
};

const CreditAllocation = ({
  creditId,
  selectedBrand,
  selectedBrands: selectedBrandsProp,
  onSuccess,
  onCancel,
  onViewPartner,
}) => {
  // Support both single brand and multiple brands
  const selectedBrands =
    selectedBrandsProp || (selectedBrand ? [selectedBrand] : []);
  const primaryBrand = selectedBrands.length > 0 ? selectedBrands[0] : null;
  const navigate = useNavigate();
  const [loadingCredit, setLoadingCredit] = useState(false);
  const [credit, setCredit] = useState(null);
  const [allocations, setAllocations] = useState([]);
  const [allocationStatus, setAllocationStatus] = useState(null);
  const [partners, setPartners] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [loadingAllocation, setLoadingAllocation] = useState(false);
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState([]);
  const [showPaidInvoices, setShowPaidInvoices] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
  });
  // Store all partners (unfiltered) for when credit loads
  const [allPartnersCache, setAllPartnersCache] = useState([]);
  // Background loading state for hybrid loading
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const backgroundLoadingRef = useRef(false);
  const backgroundAbortRef = useRef(false);

  // Refs to prevent duplicate requests
  const initialFetchDoneRef = useRef(false);
  const fetchingCreditRef = useRef(false);
  const fetchingInvoicesRef = useRef(false);
  const fetchingPartnersRef = useRef(false);
  const lastFetchKeyRef = useRef(null);

  // Fetch credit details
  const fetchCredit = useCallback(async () => {
    // Prevent duplicate requests
    if (fetchingCreditRef.current) return;
    fetchingCreditRef.current = true;

    setLoadingCredit(true);
    try {
      const data = await getCreditById(creditId);
      setCredit(data);

      // Credit data now includes credit_allocations, allocated_amount, and allocation_status
      // directly from the backend
      if (data.credit_allocations) {
        setAllocations(data.credit_allocations);
      }

      // Update allocation status using data from backend
      updateAllocationStatus(data);

      // If credit has a partner, pre-select it and fetch invoices
      // Check multiple possible field names for partner ID
      // Handle case where partner might be an object with id property
      let partnerId =
        data.invoice_partner ||
        data.partner ||
        data.partner_id ||
        data.invoice_partner_id;

      // Extract ID if partnerId is an object
      if (partnerId && typeof partnerId === "object") {
        partnerId = partnerId.id || partnerId.partner_id;
      }

      if (partnerId) {
        // Only set the partner - useEffect on selectedPartner will handle fetching invoices
        setSelectedPartner(partnerId);
      }
    } catch (error) {
      console.error("Error fetching credit:", error);

      // Check if it's a 404 error (credit not found)
      if (error.response && error.response.status === 404) {
        notification.error({
          message: "Credit Not Found",
          description: `Credit with ID ${creditId} does not exist.`,
        });
        // Navigate back to credits after a delay
        setTimeout(() => {
          handleBack();
        }, 2000);
      } else {
        notification.error({
          message: "Error",
          description: "Failed to load credit details",
        });
      }
    } finally {
      setLoadingCredit(false);
      fetchingCreditRef.current = false;
    }
  }, [creditId]);

  // Fetch credit data
  useEffect(() => {
    if (!creditId) return;
    fetchCredit();
  }, [creditId, fetchCredit]);

  // Fetch all partners as soon as selectedBrands is available (don't wait for credit)
  // This ensures the dropdown is populated when the credit loads with a partner
  useEffect(() => {
    if (!selectedBrands || selectedBrands.length === 0) {
      setPartners([]);
      setAllPartnersCache([]);
      return;
    }

    const fetchPartners = async () => {
      // Prevent duplicate requests
      if (fetchingPartnersRef.current) return;
      fetchingPartnersRef.current = true;

      try {
        // Fetch partners for all selected brands
        const brandIds = selectedBrands.map((b) => b.id).join(",");
        const data = await getAllPartners({
          brand: brandIds,
        });

        // Deduplicate partners by ID (same partner can exist across multiple brands)
        const rawPartnersList = Array.isArray(data.data) ? data.data : [];
        const seenIds = new Set();
        const seenNames = new Set();
        const partnersList = rawPartnersList.filter((partner) => {
          if (seenIds.has(partner.id)) {
            return false;
          }
          const normalizedName = (partner.name || "").toLowerCase().trim();
          if (seenNames.has(normalizedName)) {
            return false;
          }
          seenIds.add(partner.id);
          seenNames.add(normalizedName);
          return true;
        });

        setAllPartnersCache(partnersList);

        // If credit is already loaded, filter partners based on credit type
        if (credit) {
          const creditType = credit.credit_type || "bill";
          let filteredPartners = partnersList;
          if (creditType === "bill") {
            filteredPartners = partnersList.filter(
              (partner) =>
                partner.partner_type === "vendor" ||
                partner.partner_type === "both"
            );
          } else if (creditType === "invoice") {
            filteredPartners = partnersList.filter(
              (partner) =>
                partner.partner_type === "customer" ||
                partner.partner_type === "both"
            );
          }
          setPartners(filteredPartners);
        } else {
          // Credit not loaded yet, show all partners
          setPartners(partnersList);
        }
      } catch (error) {
        console.error("Error fetching partners:", error);
        setPartners([]);
        setAllPartnersCache([]);
      } finally {
        fetchingPartnersRef.current = false;
      }
    };

    fetchPartners();
  }, [selectedBrands]); // Only depend on selectedBrands, not credit

  // Re-filter partners when credit loads (to filter by vendor/customer based on credit type)
  useEffect(() => {
    if (!credit || allPartnersCache.length === 0) return;

    const creditType = credit.credit_type || "bill";
    let filteredPartners = allPartnersCache;

    if (creditType === "bill") {
      // For bill credits, only show vendors
      filteredPartners = allPartnersCache.filter(
        (partner) =>
          partner.partner_type === "vendor" || partner.partner_type === "both"
      );
    } else if (creditType === "invoice") {
      // For invoice credits, only show customers
      filteredPartners = allPartnersCache.filter(
        (partner) =>
          partner.partner_type === "customer" || partner.partner_type === "both"
      );
    }

    setPartners(filteredPartners);
  }, [credit, allPartnersCache]);

  // Helper to process invoice data
  const processInvoiceData = useCallback(
    (data) => {
      return data.map((invoice) => {
        // Check if the invoice has existing allocation data from backend
        const currentAllocationId = invoice.current_allocation_id || null;
        const currentAllocationAmount = parseFloat(
          invoice.current_allocation_amount || 0
        );

        // Calculate remaining amount correctly
        const totalAmount = parseFloat(invoice.total_amount || 0);
        const paidAmount = parseFloat(invoice.paid_amount || 0);
        const remainingAmount = totalAmount - paidAmount;

        // Check if this invoice is allocated in the credit allocations
        const existingAllocation = allocations.find(
          (alloc) =>
            alloc.invoice === invoice.id || alloc.invoice_id === invoice.id
        );

        // Use the larger of the two allocation amounts (in case backend data is outdated)
        const allocationAmount = Math.max(
          currentAllocationAmount,
          existingAllocation ? parseFloat(existingAllocation.amount || 0) : 0
        );

        return {
          ...invoice,
          creditAmount: allocationAmount > 0 ? allocationAmount : "",
          creditNotes: "",
          remainingAmount: remainingAmount,
          totalAmount,
          paidAmount,
          is_allocated: allocationAmount > 0,
        };
      });
    },
    [allocations]
  );

  // Fetch invoices function - HYBRID LOADING: first page fast, rest in background
  const fetchInvoices = async (partnerId, showPaidInvoices = false) => {
    if (!partnerId || !selectedBrands || selectedBrands.length === 0) return;

    // Create a unique key for this request to prevent duplicates
    const fetchKey = `${creditId}-${partnerId}-${showPaidInvoices}`;
    if (fetchingInvoicesRef.current && lastFetchKeyRef.current === fetchKey) {
      return; // Skip duplicate request
    }

    fetchingInvoicesRef.current = true;
    lastFetchKeyRef.current = fetchKey;

    // Abort any ongoing background loading
    backgroundAbortRef.current = true;
    setLoadingInvoices(true);

    try {
      // Pass all brand IDs for cross-brand allocation
      const brandIds = selectedBrands.map((b) => b.id).join(",");
      const baseParams = {
        brand: brandIds,
        partner: partnerId,
        credit_id: creditId,
        show_paid_invoices: showPaidInvoices,
      };

      // ========== PHASE 1: Quick first page load ==========
      const firstPageResult = await getFirstPagePartnerInvoicesForCredit(
        baseParams
      );
      const firstPageData = firstPageResult.results || [];

      // Process and display first page immediately
      const processedFirstPage = processInvoiceData(firstPageData);
      setInvoices(processedFirstPage);

      // Update selected invoice IDs for allocated invoices
      const allocatedIds = processedFirstPage
        .filter((inv) => inv.is_allocated)
        .map((inv) => inv.id);
      if (allocatedIds.length > 0) {
        setSelectedInvoiceIds((prev) => [
          ...new Set([...prev, ...allocatedIds]),
        ]);
      }

      setLoadingInvoices(false);

      // ========== PHASE 2: Load remaining data in background ==========
      if (firstPageResult.hasMore) {
        backgroundAbortRef.current = false;
        backgroundLoadingRef.current = true;
        setIsLoadingMore(true);

        // Small delay to let UI render
        setTimeout(async () => {
          try {
            // Fetch ALL data (getCreditPartnerInvoices fetches all pages)
            const fullResponse = await getCreditPartnerInvoices(baseParams);

            // Check if we should abort
            if (backgroundAbortRef.current) {
              backgroundLoadingRef.current = false;
              setIsLoadingMore(false);
              return;
            }

            // Handle different response formats
            let fullData;
            if (Array.isArray(fullResponse)) {
              fullData = fullResponse;
            } else if (fullResponse && Array.isArray(fullResponse.data)) {
              fullData = fullResponse.data;
            } else if (fullResponse && Array.isArray(fullResponse.results)) {
              fullData = fullResponse.results;
            } else {
              fullData = [];
            }

            // Process and update with full data
            const processedFull = processInvoiceData(fullData);
            setInvoices(processedFull);

            // Update selected invoice IDs
            const allAllocatedIds = processedFull
              .filter((inv) => inv.is_allocated)
              .map((inv) => inv.id);
            if (allAllocatedIds.length > 0) {
              setSelectedInvoiceIds((prev) => [
                ...new Set([...prev, ...allAllocatedIds]),
              ]);
            }
          } catch (bgError) {
            console.error("Background loading error:", bgError);
          } finally {
            backgroundLoadingRef.current = false;
            setIsLoadingMore(false);
          }
        }, 100);
      }
    } catch (error) {
      console.error("Error fetching invoices:", error);
      setInvoices([]);
      setLoadingInvoices(false);

      let errorMessage = "Failed to load partner invoices";
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }

      notification.error({
        message: "Invoice Loading Error",
        description: errorMessage,
      });
    } finally {
      // Allow new requests after a short delay to prevent rapid re-fetches
      setTimeout(() => {
        fetchingInvoicesRef.current = false;
      }, 500);
    }
  };

  // Fetch invoices when partner or brand changes (showPaidInvoices is handled in its own handler)
  useEffect(() => {
    if (selectedPartner) {
      fetchInvoices(selectedPartner, showPaidInvoices);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPartner, selectedBrands]);

  // Handle partner change - useEffect on selectedPartner will handle fetching invoices
  const handlePartnerChange = (value) => {
    setSelectedPartner(value);
    // Clear invoices when partner changes
    setInvoices([]);
    // Invoice fetching is handled by useEffect on selectedPartner
  };

  // Handle credit allocation
  const handleAllocateCredit = async (
    invoiceId,
    amount,
    allocationId = null
  ) => {
    // Find the invoice to get its number for messages
    const invoice = invoices.find((inv) => inv.id === invoiceId);
    const invoiceNumber = invoice ? invoice.number : invoiceId;

    // Convert amount to number if it's a string (handle commas)
    const creditAmount =
      typeof amount === "string" ? parseNumberWithCommas(amount) : amount;

    // Check if this is a deallocation (amount is zero)
    const isDeallocation = creditAmount === 0;

    // For allocation (not deallocation), validate amount is positive
    if (
      !isDeallocation &&
      (!creditAmount || creditAmount <= 0 || isNaN(creditAmount))
    ) {
      notification.error({
        message: "Validation Error",
        description: "Please enter a valid credit amount greater than 0",
      });
      return;
    }

    // Find the invoice to validate against remaining amount
    const invoiceToAllocate = invoices.find((inv) => inv.id === invoiceId);
    if (!invoiceToAllocate) {
      notification.error({
        message: "Validation Error",
        description: "Invoice not found",
      });
      return;
    }

    // Prevent allocation to cancelled invoices
    if (!isDeallocation && invoiceToAllocate.status === "cancelled") {
      notification.error({
        message: "Validation Error",
        description: `Cannot allocate credit to cancelled invoice #${
          invoiceToAllocate.number || invoiceNumber
        }. Cancelled invoices cannot receive allocations.`,
      });
      return;
    }

    // Only validate remaining amount for new allocations (not deallocations)
    if (!isDeallocation) {
      const remainingAmount = parseFloat(invoiceToAllocate.remainingAmount);
      const epsilon = 0.001; // Small tolerance for floating point comparison

      // If updating an existing allocation, add its current amount to the remaining amount
      let adjustedRemainingAmount = remainingAmount;
      if (allocationId && invoiceToAllocate.current_allocation_id) {
        adjustedRemainingAmount += parseFloat(
          invoiceToAllocate.current_allocation_amount || 0
        );
      }

      if (creditAmount > adjustedRemainingAmount + epsilon) {
        notification.error({
          message: "Validation Error",
          description: `Credit amount (${creditAmount.toFixed(
            2
          )}) exceeds the remaining balance (${adjustedRemainingAmount.toFixed(
            2
          )}) for invoice #${invoiceToAllocate.number}`,
        });
        return;
      }
    }

    // Validate credit has enough unallocated amount
    if (credit && !isDeallocation) {
      const creditAmount = parseFloat(credit.total_amount || 0);
      const allocatedAmount = parseFloat(credit.allocated_amount || 0);
      const remainingCredit = creditAmount - allocatedAmount;

      // If updating an existing allocation, add its current amount to the remaining amount
      let adjustedRemainingCredit = remainingCredit;
      if (allocationId && invoiceToAllocate.current_allocation_id) {
        adjustedRemainingCredit += parseFloat(
          invoiceToAllocate.current_allocation_amount || 0
        );
      }

      // Add a small epsilon to handle floating point precision issues
      const epsilon = 0.001;

      if (amount > adjustedRemainingCredit + epsilon) {
        notification.error({
          message: "Validation Error",
          description: `Credit amount (${amount}) exceeds the remaining credit amount (${adjustedRemainingCredit.toFixed(
            2
          )})`,
        });
        return;
      }
    }

    try {
      setLoadingAllocation(true);

      // Prepare allocation data
      const allocationData = {
        invoice_id: invoiceId,
        amount: creditAmount,
        partner_id: selectedPartner,
      };

      // If this is updating an existing allocation, include the allocation_id
      if (allocationId) {
        allocationData.allocation_id = allocationId;
      }

      const result = await allocateCredit(creditId, allocationData);

      // Show success notification with the correct invoice number
      if (isDeallocation) {
        // For unallocation, immediately update the UI state
        // This ensures the button changes from Unallocate to Allocate right away
        setInvoices((prevInvoices) => {
          return prevInvoices.map((inv) => {
            if (inv.id === invoiceId) {
              return {
                ...inv,
                current_allocation_id: null,
                current_allocation_amount: 0,
                creditAmount: "",
                is_allocated: false,
              };
            }
            return inv;
          });
        });

        // Update allocations array to remove this allocation
        setAllocations((prevAllocations) =>
          prevAllocations.filter(
            (alloc) =>
              !(alloc.invoice === invoiceId || alloc.invoice_id === invoiceId)
          )
        );

        notification.success({
          message: "Credit Unallocated",
          description: `Successfully unallocated credit from invoice #${invoiceNumber}`,
        });
      } else {
        // Use creditAmount (the parsed value) for the notification, formatted to 3 decimal places
        notification.success({
          message: "Credit Allocated",
          description: `Successfully ${
            allocationId ? "updated" : "allocated"
          } ${creditAmount.toFixed(getDecimalPrecision())} to invoice #${invoiceNumber}`,
        });
      }

      // Clear all caches to ensure fresh data on next page load
      clearAccountingCache();
      clearPartnerInvoicesCache();

      // Notify parent component with creditId to update just that row
      if (onSuccess) {
        onSuccess({ creditId, type: "credit" });
      }

      // For deallocation, we already updated local state - don't refetch immediately
      // as it can return stale cached data. Wait a moment for backend to process.
      if (isDeallocation) {
        // Refetch credit data after a short delay to allow backend to process
        setTimeout(() => {
          fetchCredit();
          if (selectedPartner) {
            fetchInvoices(selectedPartner, showPaidInvoices);
          }
        }, 500);
      } else {
        // For allocation, refetch immediately
        fetchCredit();
        if (selectedPartner) {
          fetchInvoices(selectedPartner, showPaidInvoices);
        }
      }
    } catch (error) {
      console.error("Error allocating credit:", error);

      // Provide more specific error messages
      let errorMessage = "Failed to allocate credit";

      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.status === 400) {
        errorMessage =
          "Invalid credit data. The amount may exceed the available credit amount or invoice remaining balance.";
      } else if (error.response?.status === 404) {
        errorMessage = "Invoice or credit not found.";
      } else if (error.response?.status === 403) {
        errorMessage = "You do not have permission to allocate this credit.";
      }

      notification.error({
        message: "Allocation Error",
        description: errorMessage,
      });
    } finally {
      setLoadingAllocation(false);
    }
  };

  const handleCreditChange = (invoiceId, e) => {
    // Get the raw input value
    const inputValue = e.target.value;

    // Update the credit amount for the specific invoice
    const updatedInvoices = invoices.map((invoice) => {
      if (invoice.id === invoiceId) {
        // Store the raw input value to prevent cursor jumping
        // Only convert to number when actually submitting
        return {
          ...invoice,
          creditAmount: inputValue,
        };
      }
      return invoice;
    });

    setInvoices(updatedInvoices);

    // Auto-check the invoice if a valid credit amount is entered
    const numericValue = parseFloat(inputValue);
    if (!isNaN(numericValue) && numericValue > 0) {
      // Add to selected invoices if not already selected
      if (!selectedInvoiceIds.includes(invoiceId)) {
        setSelectedInvoiceIds((prev) => [...prev, invoiceId]);
      }
    } else {
      // Remove from selected invoices if credit amount is cleared or invalid
      setSelectedInvoiceIds((prev) => prev.filter((id) => id !== invoiceId));
    }
  };

  // Handle row selection change (for multiple selection)
  const handleRowSelectionChange = (selectedRowKeys) => {
    setSelectedInvoiceIds(selectedRowKeys);
  };

  // Handle allocate all checked invoices
  const handleAllocateAllChecked = async () => {
    if (selectedInvoiceIds.length === 0) {
      notification.warning({
        message: "No Invoices Selected",
        description: "Please select at least one invoice to allocate credit.",
      });
      return;
    }

    setLoadingAllocation(true);

    try {
      // Process each selected invoice one by one
      for (const invoiceId of selectedInvoiceIds) {
        const invoice = invoices.find((inv) => inv.id === invoiceId);

        if (!invoice) continue;

        // Skip invoices that are already fully paid, cancelled, or have no remaining balance
        if (
          parseFloat(invoice.remainingAmount) <= 0 ||
          invoice.status === "paid" ||
          invoice.status === "cancelled"
        ) {
          continue;
        }

        // Get the allocation ID if it exists
        const allocationId = invoice.current_allocation_id;

        // Use the entered credit amount if available, otherwise use the remaining amount
        // IMPORTANT: Use parseNumberWithCommas to handle comma-formatted numbers (e.g., "2,007.430")
        let amount;
        const parsedCreditAmount = parseNumberWithCommas(invoice.creditAmount);
        if (parsedCreditAmount > 0) {
          amount = parsedCreditAmount;
        } else {
          amount = parseFloat(invoice.remainingAmount);
        }

        // Allocate credit to this invoice
        await handleAllocateCredit(invoice.id, amount, allocationId);
      }
    } catch (error) {
      console.error("Error in bulk allocation:", error);
      notification.error({
        message: "Bulk Allocation Error",
        description:
          "An error occurred while allocating credit to multiple invoices.",
      });
    } finally {
      setLoadingAllocation(false);
    }
  };

  // Update allocation status from credit data
  const updateAllocationStatus = (credit) => {
    if (!credit) {
      setAllocationStatus(null);
      return;
    }

    try {
      // Set allocations from credit data
      if (credit.credit_allocations) {
        setAllocations(credit.credit_allocations);
      }

      // Use allocation status directly from backend if available
      if (credit.allocation_status) {
        setAllocationStatus({
          ...credit.allocation_status,
          totalAllocated: parseFloat(credit.allocated_amount || 0),
          percentAllocated: credit.percent_allocated || 0,
        });
        return;
      }

      // Fallback calculation if backend doesn't provide allocation status
      const creditAmount = Math.abs(parseFloat(credit.total_amount || 0));
      const totalAllocated = parseFloat(credit.allocated_amount || 0);
      const percentAllocated =
        creditAmount > 0 ? (totalAllocated / creditAmount) * 100 : 0;

      // Determine status based on remaining amount (more accurate than percentage)
      const remainingAmount = creditAmount - totalAllocated;
      let status = null;
      // Only mark as fully allocated if remaining is essentially 0
      if (remainingAmount <= 0.001) {
        status = {
          type: "fully_allocated",
          label: "Fully Allocated",
          color: "success",
        };
      } else if (totalAllocated > 0) {
        status = {
          type: "partially_allocated",
          label: "Partially Allocated",
          color: "warning",
        };
      } else {
        status = {
          type: "unallocated",
          label: "Unallocated",
          color: "default",
        };
      }

      setAllocationStatus({
        ...status,
        totalAllocated,
        percentAllocated,
      });
    } catch (error) {
      console.error("Error updating allocation status:", error);
      setAllocationStatus({
        type: "error",
        label: "Error",
        color: "error",
        totalAllocated: 0,
        percentAllocated: 0,
      });
    }
  };

  // Handle show paid invoices toggle - immediately refetch with new setting
  const handleShowPaidInvoicesChange = (checked) => {
    setShowPaidInvoices(checked);
    // Immediately refetch invoices with the new setting
    if (selectedPartner) {
      fetchInvoices(selectedPartner, checked);
    }
  };

  // Sort invoices by oldest to newest (issue_date ascending) for allocation
  const sortedInvoices = useMemo(() => {
    return [...invoices].sort((a, b) => {
      const dateA = a.issue_date ? new Date(a.issue_date) : new Date(0);
      const dateB = b.issue_date ? new Date(b.issue_date) : new Date(0);
      return dateA - dateB; // Oldest first
    });
  }, [invoices]);

  // Handle back button
  const handleBack = () => {
    if (onCancel) {
      onCancel();
    } else {
      navigate("/accounting/credits");
    }
  };

  if (!creditId || (!selectedBrand && !primaryBrand)) {
    return (
      <Card title="Credit Allocation">
        <Text type="secondary">No credit selected or brand not selected</Text>
        <div style={{ marginTop: 16 }}>
          <Button
            type="primary"
            icon={<ArrowLeftOutlined />}
            onClick={handleBack}
          >
            Back to Credits
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card size="small" className="credit-allocation-card">
      {loadingCredit ? (
        <div style={{ textAlign: "center", padding: "50px" }}>
          <Spin size="large" />
        </div>
      ) : (
        <>
          <Title level={5}>Credit Details</Title>
          {credit && (
            <Table
              dataSource={[credit]}
              columns={getCreditColumns(allocationStatus)}
              pagination={false}
              rowKey="id"
              size="small"
            />
          )}

          <Divider />

          <Row gutter={16} justify="space-between" align="middle">
            <Col span={8}>
              <div style={{ marginBottom: 16 }}>
                <Text strong>Select Partner:</Text>
                <Select
                  placeholder="Select a partner"
                  style={{ width: "100%", marginTop: 8 }}
                  onChange={handlePartnerChange}
                  value={selectedPartner}
                  showSearch
                  optionFilterProp="children"
                  // disabled={credit && credit.partner} // Disable if credit already has a partner
                >
                  {Array.isArray(partners) && partners.length > 0 ? (
                    partners.map((partner) => (
                      <Option key={partner.id} value={partner.id}>
                        {partner.name} ({partner.partner_type})
                      </Option>
                    ))
                  ) : (
                    <Option value="" disabled>
                      No partners available
                    </Option>
                  )}
                </Select>
              </div>
            </Col>
            <Col>
              <div
                style={{
                  marginBottom: 16,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Space>
                  <span>Show Paid</span>
                  <Switch
                    checked={showPaidInvoices}
                    onChange={handleShowPaidInvoicesChange}
                  />
                </Space>
              </div>
            </Col>
          </Row>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: 16,
              marginBottom: 16,
            }}
          >
            <Title level={5} style={{ margin: 0 }}>
              Partner Invoices
            </Title>
            <Button
              type="primary"
              onClick={handleAllocateAllChecked}
              disabled={loadingAllocation || selectedInvoiceIds.length === 0}
              loading={loadingAllocation}
            >
              Allocate All Checked
            </Button>
          </div>
          <div className="fixed-table-container">
            <Table
              dataSource={sortedInvoices}
              columns={getInvoiceColumns(
                handleCreditChange,
                handleAllocateCredit,
                loadingAllocation,
                parseNumberWithCommas,
                onViewPartner
              )}
              pagination={{
                ...pagination,
                total: sortedInvoices.length,
                showSizeChanger: true,
                showQuickJumper: true,
                pageSizeOptions: ["50", "100", "200"],
                showTotal: (total) => `Total ${total} invoices`,
                onChange: (page, pageSize) => {
                  setPagination({ current: page, pageSize });
                },
                onShowSizeChange: (current, size) => {
                  setPagination({ current: 1, pageSize: size });
                },
              }}
              rowKey="id"
              size="small"
              loading={loadingInvoices}
              rowClassName={(record) =>
                record.is_allocated ? "table-success-outline-invoice" : ""
              }
              scroll={{ y: 400 }}
              tableLayout="auto"
              sticky={{ offsetHeader: 0 }}
              rowSelection={{
                selectedRowKeys: selectedInvoiceIds,
                onChange: handleRowSelectionChange,
                getCheckboxProps: (record) => ({
                  disabled:
                    parseFloat(record.remainingAmount) <= 0 ||
                    record.status === "paid" ||
                    record.status === "cancelled",
                }),
              }}
            />
          </div>
        </>
      )}
    </Card>
  );
};

export default CreditAllocation;
